class CartAdditionModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.currentProduct = null;
    this.cartData = null;
    this.productMetafieldsCache = new Map(); // Cache for product metafields
    this.memberPricingThreshold = 6;

    // Bind methods
    this.handleClose = this.handleClose.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleContinueShopping = this.handleContinueShopping.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleOverlayClick);
    this.addEventListener('keydown', this.handleKeyDown);

    // Close button
    const closeBtn = this.querySelector('.cart-addition-modal__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.handleClose);
    }

    // Continue shopping button
    const continueBtn = this.querySelector('.cart-addition-modal__continue-btn');
    if (continueBtn) {
      continueBtn.addEventListener('click', this.handleContinueShopping);
    }

    // Listen for cart updates to refresh data
    document.addEventListener('cart:updated', () => {
      if (this.isOpen) {
        this.updateCartData();
      }
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleOverlayClick);
    this.removeEventListener('keydown', this.handleKeyDown);
  }

  async show(productData, cartData) {
    this.currentProduct = productData;
    this.cartData = cartData;

    // Set up ARIA attributes for modal
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    this.setAttribute('aria-labelledby', 'cart-addition-modal-title');
    this.setAttribute('aria-describedby', 'cart-addition-modal-description');

    // Fetch metafields for current product and all cart items
    // This ensures we have accurate bundle information for all products
    await Promise.all([this.fetchProductMetafields(), this.fetchAllCartItemsMetafields()]);

    await this.updateDisplay();
    this.open();
  }

  async fetchProductMetafields() {
    if (!this.currentProduct) return;

    try {
      // Try to get product handle from the current product data
      let productHandle = null;

      // Check if we have a handle in the product data
      if (this.currentProduct.handle) {
        productHandle = this.currentProduct.handle;
      } else if (this.currentProduct.url) {
        // Extract handle from URL (e.g., /products/wine-bundle-6-pack)
        const match = this.currentProduct.url.match(/\/products\/([^/?]+)/);
        if (match) {
          productHandle = match[1];
        }
      }

      // If we couldn't get the handle, try to find it from the cart data
      if (!productHandle && this.cartData && this.cartData.items) {
        const cartItem = this.cartData.items.find(
          (item) => item.variant_id === this.currentProduct.variant?.id || item.id === this.currentProduct.variant?.id,
        );
        if (cartItem && cartItem.handle) {
          productHandle = cartItem.handle;
        }
      }

      if (productHandle) {
        // Check cache first
        if (this.productMetafieldsCache.has(productHandle)) {
          const cachedData = this.productMetafieldsCache.get(productHandle);
          this.currentProduct.metafields = cachedData.metafields;
          this.currentProduct.is_bundle = cachedData.is_bundle;
          this.currentProduct.pack_size = cachedData.pack_size;
          console.log('Using cached metafields for:', productHandle);
          return;
        }

        const response = await fetch(`/products/${productHandle}?view=metafields`);
        if (response.ok) {
          const metafieldsData = await response.json();

          // Cache the data
          this.productMetafieldsCache.set(productHandle, metafieldsData);

          // Merge metafields data with current product data
          this.currentProduct.metafields = metafieldsData.metafields;
          this.currentProduct.is_bundle = metafieldsData.is_bundle;
          this.currentProduct.pack_size = metafieldsData.pack_size;
        }
      }
    } catch (error) {
      console.error('Failed to fetch product metafields:', error);
      // Continue without metafields - fallback to existing logic
    }
  }

  async fetchAllCartItemsMetafields() {
    if (!this.cartData || !this.cartData.items) return;

    // Create array of promises to fetch all metafields in parallel
    const fetchPromises = this.cartData.items.map(async (item) => {
      // Skip if not a product or if we already have the data
      if (item.product_type === 'membership' || !item.handle) return;

      // Check cache first
      if (this.productMetafieldsCache.has(item.handle)) {
        const cachedData = this.productMetafieldsCache.get(item.handle);
        // Attach metafields to the item
        item.metafields = cachedData.metafields;
        item.is_bundle = cachedData.is_bundle;
        item.pack_size = cachedData.pack_size;
        return;
      }

      try {
        const response = await fetch(`/products/${item.handle}?view=metafields`);
        if (response.ok) {
          const metafieldsData = await response.json();

          // Cache the data
          this.productMetafieldsCache.set(item.handle, metafieldsData);

          // Attach metafields to the item
          item.metafields = metafieldsData.metafields;
          item.is_bundle = metafieldsData.is_bundle;
          item.pack_size = metafieldsData.pack_size;
        }
      } catch (error) {
        console.error(`Failed to fetch metafields for ${item.handle}:`, error);
      }
    });

    // Wait for all fetches to complete
    await Promise.all(fetchPromises);
  }

  async updateDisplay() {
    if (!this.currentProduct || !this.cartData) return;

    // Update product information
    this.updateProductInfo();

    // Update cart totals
    this.updateCartTotals();

    // Update member pricing progress
    this.updateProgress();
  }

  updateProductInfo() {
    const product = this.currentProduct;

    // Product image
    const imageEl = this.querySelector('#cart-addition-product-image');
    if (imageEl && product.image) {
      imageEl.src = product.image;
      imageEl.alt = product.title || '';
    }

    // Product badge (Fine Wine Collection)
    const badgeEl = this.querySelector('#cart-addition-product-badge');
    if (badgeEl) {
      if (product.isFineWine) {
        badgeEl.textContent = 'Fine Wine Collection';
        badgeEl.style.display = 'inline-block';
      } else {
        badgeEl.style.display = 'none';
      }
    }

    // Variant badge - clone from product page if exists
    this.updateVariantBadge(product);

    // Product title
    const titleEl = this.querySelector('#cart-addition-product-title');
    if (titleEl) {
      titleEl.textContent = product.title || '';
    }

    // Product variant/subtitle - use the subtitle from card-product-simple
    const variantEl = this.querySelector('#cart-addition-product-variant');
    if (variantEl) {
      if (product.subtitle) {
        variantEl.textContent = product.subtitle;
      } else if (product.variant && product.variant.title) {
        variantEl.textContent = product.variant.title;
      } else {
        variantEl.style.display = 'none';
      }
    }

    // Product price
    const priceEl = this.querySelector('#cart-addition-product-price');
    if (priceEl) {
      priceEl.textContent = this.formatMoney(product.price);
    }

    // Product quantity - show pack size for bundles
    const quantityEl = this.querySelector('#cart-addition-product-quantity');
    const quantityLabelEl = this.querySelector('#cart-addition-quantity-label');
    if (quantityEl) {
      // If it's a bundle, show the pack size times the quantity
      if (product.is_bundle && product.pack_size) {
        const totalBottles = product.pack_size * (product.quantity || 1);
        quantityEl.textContent = totalBottles;
        // Update label to show "Bottles" for bundles
        if (quantityLabelEl) {
          quantityLabelEl.textContent = totalBottles === 1 ? 'Bottle' : 'Bottles';
        }
      } else {
        quantityEl.textContent = product.quantity || 1;
        // Keep standard "Qty" label for non-bundles
        if (quantityLabelEl) {
          quantityLabelEl.textContent = 'Qty';
        }
      }
    }

    // Wine colors for bundles
    this.updateWineColors(product);

    // Low stock warning
    const stockWarningEl = this.querySelector('.cart-addition-modal__hurry_stock');
    if (stockWarningEl && product.isLowStock) {
      stockWarningEl.innerHTML = `
        <span class="cart-addition-modal__inventory-warning">
          <span class="cart-addition-modal__inventory-warning-icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M8 5V8L10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </span>
          <span>Hurry! Low stock</span>
        </span>
      `;
      stockWarningEl.style.display = 'inline-flex';
    } else if (stockWarningEl) {
      stockWarningEl.innerHTML = '';
      stockWarningEl.style.display = 'none';
    }
  }

  calculateTotalBottles() {
    if (!this.cartData || !this.cartData.items) return 0;

    let totalBottles = 0;

    for (const item of this.cartData.items) {
      // Skip membership items
      if (item.product_type === 'membership') {
        continue;
      }

      let itemBottles = 0;

      // Check if item is a bundle with item_components (Shopify native bundle)
      if (item.item_components && Array.isArray(item.item_components)) {
        // Count total quantity from bundle components
        for (const component of item.item_components) {
          itemBottles += component.quantity || 1;
        }
      }
      // Check for bundle properties in item properties
      else if (item.properties && (item.properties._pack_size || item.properties._bundle_parts)) {
        // Check for _pack_size property (our custom bundle implementation)
        if (item.properties._pack_size) {
          const packSize = parseInt(item.properties._pack_size) || 1;
          itemBottles = packSize * (item.quantity || 1);
        }
        // Check for _bundle_parts property (detailed bundle information)
        else if (item.properties._bundle_parts) {
          try {
            // Unescape HTML entities and parse JSON
            const unescaped = item.properties._bundle_parts
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&amp;/g, '&');

            const bundleParts = JSON.parse(unescaped);
            if (Array.isArray(bundleParts)) {
              let bundleTotal = bundleParts.length; // Count number of parts
              itemBottles = bundleTotal * (item.quantity || 1);
            }
          } catch (e) {
            console.error('Failed to parse bundle parts:', e);
            // Fallback: use _pack_size if available
            if (item.properties._pack_size) {
              const packSize = parseInt(item.properties._pack_size) || 1;
              itemBottles = packSize * (item.quantity || 1);
            } else {
              itemBottles = item.quantity || 1;
            }
          }
        }
      } else {
        // Regular item without special properties
        itemBottles = item.quantity || 1;
      }

      totalBottles += itemBottles;
    }

    return totalBottles;
  }

  updateCartTotals() {
    if (!this.cartData) return;

    const totalItemsEl = this.querySelector('#cart-addition-total-items');
    if (totalItemsEl) {
      // Use the same logic as updateShippingProgress for consistency
      const quantityEl = this.querySelector('#cart-addition-product-quantity');
      let totalBottles = 0;

      // Get current product bottles from the display (already calculated correctly)
      if (quantityEl && quantityEl.textContent) {
        const currentProductBottles = parseInt(quantityEl.textContent) || 0;

        // Calculate bottles from other items in cart
        let otherBottles = 0;
        if (this.cartData.items && this.cartData.items.length > 0) {
          // Find the current item in the cart to avoid double counting
          const currentItemIndex = this.cartData.items.findIndex((item) => {
            if (this.currentProduct && this.currentProduct.variant?.id) {
              return item.variant_id === this.currentProduct.variant.id;
            }
            return false;
          });

          // Count bottles from all items except the current one
          this.cartData.items.forEach((item, index) => {
            if (index === currentItemIndex) return;
            if (item.product_type === 'membership') return;

            // Use metafields first (most accurate)
            if (item.is_bundle && item.pack_size) {
              otherBottles += item.pack_size * (item.quantity || 1);
            }
            // Then check properties
            else if (item.properties && item.properties._pack_size) {
              const packSize = parseInt(item.properties._pack_size) || 1;
              otherBottles += packSize * (item.quantity || 1);
            }
            // Check for native bundles
            else if (item.item_components && Array.isArray(item.item_components)) {
              item.item_components.forEach((component) => {
                otherBottles += component.quantity || 1;
              });
            }
            // Regular item
            else {
              otherBottles += item.quantity || 1;
            }
          });
        }

        totalBottles = currentProductBottles + otherBottles;
      } else {
        // Fallback to original calculation
        totalBottles = this.calculateTotalBottles();
      }

      totalItemsEl.textContent = totalBottles;
    }

    // Update cart count bubble in header
    if (window.CartCountUpdater) {
      window.CartCountUpdater.updateCartIcon(this.cartData);
    }
  }

  updateWineColors(product) {
    const wineColorsEl = this.querySelector('#cart-addition-wine-colors');
    const wineColorsListEl = this.querySelector('#cart-addition-wine-colors-list');

    if (!wineColorsEl || !wineColorsListEl) return;

    // Only show wine colors for bundles
    if (!product.is_bundle || !product.metafields?.firstleaf?.bundle_parts) {
      wineColorsEl.style.display = 'none';
      return;
    }

    // Count wine colors from bundle parts
    const colorCounts = {};
    const bundleParts = product.metafields.firstleaf.bundle_parts;

    bundleParts.forEach((part) => {
      if (part.color) {
        const color = part.color.toLowerCase();
        colorCounts[color] = (colorCounts[color] || 0) + 1;
      }
    });

    // Format the wine colors display
    const colorEntries = Object.entries(colorCounts);
    if (colorEntries.length === 0) {
      wineColorsEl.style.display = 'none';
      return;
    }

    // Create readable color list
    const colorText = colorEntries
      .map(([color, count]) => {
        const capitalizedColor = color.charAt(0).toUpperCase() + color.slice(1);
        return count === 1 ? capitalizedColor : `${count} ${capitalizedColor}s`;
      })
      .join(', ');

    wineColorsListEl.textContent = colorText;
    wineColorsEl.style.display = 'block';
  }

  updateVariantBadge(product) {
    const productBadgeEl = this.querySelector('#cart-addition-product-badge');
    if (!productBadgeEl || !product.variant.id) return;

    // Clear existing badge content
    productBadgeEl.innerHTML = '';
    productBadgeEl.style.display = 'none';

    // Find badge with matching variant ID on the current page
    const badgeSelector = `[data-badge="${product.variant.id}"]`;
    const sourceBadge = document.querySelector(badgeSelector);

    if (sourceBadge) {
      // Clone the badge and its styles
      const clonedBadge = sourceBadge.cloneNode(true);

      // Copy computed styles to ensure visual consistency
      const computedStyle = window.getComputedStyle(sourceBadge);
      const styleProps = [
        'background-color',
        'color',
        'font-size',
        'font-weight',
        'padding',
        'border-radius',
        'display',
        'text-align',
        'text-transform',
        'letter-spacing',
      ];

      styleProps.forEach((prop) => {
        clonedBadge.style[prop] = computedStyle.getPropertyValue(prop);
      });

      // Add to modal
      productBadgeEl.appendChild(clonedBadge);
      productBadgeEl.style.display = 'block';
    }
  }

  updateProgress() {
    if (!this.cartData) return;

    // Get the actual bottle count displayed in the quantity element
    // This is already calculated correctly in updateProductInfo()
    const quantityEl = this.querySelector('#cart-addition-product-quantity');
    let currentProductBottles = 0;

    if (quantityEl && quantityEl.textContent) {
      currentProductBottles = parseInt(quantityEl.textContent) || 0;
    } else if (this.currentProduct) {
      // Fallback: calculate from current product
      if (this.currentProduct.is_bundle && this.currentProduct.pack_size) {
        currentProductBottles = this.currentProduct.pack_size * (this.currentProduct.quantity || 1);
      } else {
        currentProductBottles = this.currentProduct.quantity || 1;
      }
    }

    // Calculate total bottles from other items in cart (excluding current product)
    let otherBottles = 0;

    if (this.cartData.items && this.cartData.items.length > 0) {
      // Find the current item in the cart to exclude it from the count
      const currentItemIndex = this.cartData.items.findIndex((item) => {
        if (this.currentProduct.variant?.id) {
          return item.variant_id === this.currentProduct.variant.id;
        }
        return false;
      });

      // Calculate bottles from all items except the current one
      this.cartData.items.forEach((item, index) => {
        // Skip if this is the current item (we'll add it separately)
        if (index === currentItemIndex) {
          return;
        }

        // Skip membership items
        if (item.product_type === 'membership') {
          return;
        }

        // Count bottles for this item
        // First check if we have metafields data (most accurate)
        if (item.is_bundle && item.pack_size) {
          otherBottles += item.pack_size * (item.quantity || 1);
        }
        // Then check properties
        else if (item.properties && item.properties._pack_size) {
          const packSize = parseInt(item.properties._pack_size) || 1;
          otherBottles += packSize * (item.quantity || 1);
        }
        // Check for native bundles
        else if (item.item_components && Array.isArray(item.item_components)) {
          let bundleBottles = 0;
          item.item_components.forEach((component) => {
            bundleBottles += component.quantity || 1;
          });
          otherBottles += bundleBottles;
        }
        // Regular item
        else {
          otherBottles += item.quantity || 1;
        }
      });
    }

    // Total bottles = current product + other items
    const currentBottles = currentProductBottles + otherBottles;
    const progress = Math.min((currentBottles / this.memberPricingThreshold) * 100, 100);
    const bottlesNeeded = Math.max(this.memberPricingThreshold - currentBottles, 0);

    // Update progress bar
    const progressFillEl = this.querySelector('#cart-addition-progress-fill');
    if (progressFillEl) {
      progressFillEl.style.width = `${progress}%`;
    }

    // Update shipping message
    const messageEl = this.querySelector('#cart-addition-shipping-message');
    if (messageEl) {
      console.log('Current bottles:', currentBottles, 'Progress:', progress, 'Bottles needed:', bottlesNeeded);
      if (bottlesNeeded > 0) {
        console.log('Using below message');
        const belowMessage = this.getMemberPricingMessage('below');
        messageEl.textContent = belowMessage.replace('{COUNT}', bottlesNeeded);
      } else {
        console.log('Using above message');
        const aboveMessage = this.getMemberPricingMessage('above');
        messageEl.textContent = aboveMessage;
      }
    }
  }

  getFreeShippingThreshold() {
    return 12;
  }

  getMemberPricingMessage(type) {
    if (type === 'below') {
      return 'Add {COUNT} more bottles to get member pricing!';
    } else {
      return 'Congrats! You unlocked member pricing for this order!';
    }
  }

  getFreeShippingMessage(type) {
    return '';
  }

  // Enhanced method to get product variant description from cart item
  getVariantDescriptionFromCartItem(item) {
    if (!item.options_with_values || item.options_with_values.length === 0) {
      return '';
    }

    // Format variant options more naturally
    const options = item.options_with_values.map((option) => {
      // Handle common wine attributes
      if (option.name.toLowerCase() === 'year' || option.name.toLowerCase() === 'vintage') {
        return option.value;
      }
      if (option.name.toLowerCase() === 'varietal') {
        return option.value;
      }
      if (option.name.toLowerCase() === 'region') {
        return `from ${option.value}`;
      }
      return `${option.name}: ${option.value}`;
    });

    return options.join(' ');
  }

  async updateCartData() {
    try {
      // Show loading state
      this.setLoadingState(true);

      this.cartData = await window.CartUtils.fetchCart();
      await this.updateDisplay();

      // Update cart count bubble in header
      if (window.CartCountUpdater) {
        window.CartCountUpdater.updateCartIcon(this.cartData);
      }
    } catch (error) {
      console.error('Failed to update cart data:', error);
      if (window.ToastManager) {
        window.ToastManager.error('Update Failed', 'Unable to update cart information. Please refresh the page.');
      }
    } finally {
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    const content = this.querySelector('.cart-addition-modal__content');
    if (content) {
      if (isLoading) {
        content.classList.add('loading');
      } else {
        content.classList.remove('loading');
      }
    }
  }

  open() {
    this.isOpen = true;
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Focus management - trap focus in modal
    this.setAttribute('tabindex', '-1');
    this.focus();

    // Trap focus within modal
    if (window.trapFocus) {
      window.trapFocus(this);
    }

    // Announce to screen readers
    this.announceToScreenReader('Product added to cart');
  }

  close() {
    this.isOpen = false;
    this.classList.remove('is-open');
    document.body.style.overflow = '';

    // Clear product data
    this.currentProduct = null;
    this.cartData = null;
  }

  handleClose() {
    this.close();
  }

  handleOverlayClick(e) {
    // Close if clicking on overlay or container (but not on content)
    const overlay = this.querySelector('.cart-addition-modal__overlay');
    const container = this.querySelector('.cart-addition-modal__container');
    const content = this.querySelector('.cart-addition-modal__content');

    if (e.target === overlay || (e.target === container && !content.contains(e.target))) {
      this.close();
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  handleContinueShopping() {
    this.close();
  }

  formatMoney(cents) {
    if (typeof cents !== 'number') return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.style.position = 'absolute';
    announcement.style.left = '-10000px';
    announcement.style.width = '1px';
    announcement.style.height = '1px';
    announcement.style.overflow = 'hidden';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }
}

// Define the custom element
if (!customElements.get('cart-addition-modal')) {
  customElements.define('cart-addition-modal', CartAdditionModal);
}

// Export for use in other scripts
window.CartAdditionModal = CartAdditionModal;
